import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryCache, UseQueryOptions, UseMutationOptions, DefaultOptions, UseQueryResult, QueryMeta, useQuery as useQueryInternal, useInfiniteQuery as useInfiniteQueryInternal, useMutation as useMutationInternal, queryOptions, QueryKey, InfiniteData, Query, UseInfiniteQueryOptions, useQueryClient as useQueryClientInternal } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CommutifiErrorData } from '@commutifi-fe/interfaces';
import { Optional, Overwrite } from 'utility-types';
import { NotificationConfig } from '@commutifi-fe/ui';
import { type HandleErrorOptions, useNotifications } from '@commutifi-specific/hooks';
import { CommutifiError } from 'shared/interfaces';
export { queryOptions };
export type { UseQueryOptions, UseMutationOptions, UseQueryResult, QueryKey, InfiniteData };
export const queryCache = new QueryCache();
const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false
  }
};
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
  queryCache
});
interface QMeta extends QueryMeta {
  errorMessage?: string;
  /**
   * Use this if you want to set a message but also a configuration to apply to the toast like message
   * using our notification system
   */
  errorConfig?: NotificationConfig & {
    message: string;
  };
  /**
   * If true, the error will be handled by our useNotifications hook; handleError function
   * [OPTIONAL]: If there is a handleErrorConfig object, it will assume this boolean is true
   */
  shouldHandleError?: boolean;
  handleErrorConfig?: HandleErrorOptions;
}
export type ExtractFnReturnType<FnType extends (...args: any) => any, PickRes = unknown> = PickRes extends string ? Awaited<ReturnType<FnType>>[PickRes] : Awaited<ReturnType<FnType>>;

/**
 * QueryFnType: Function called to fetch the data and from which we extract the Awaited Return type of that function.
 * Res: Generic type to overwrite the Return result of the query response
 */
export type QueryConfig<QueryFnType extends (...args: any) => any, Res = ExtractFnReturnType<QueryFnType>> = Optional<Overwrite<UseQueryOptions<ExtractFnReturnType<QueryFnType>, AxiosError<CommutifiErrorData>, Res>, {
  meta?: QMeta;
}>, 'queryKey' | 'queryFn'>;

/**
 * Idem to QueryConfig but instead of allowing overwrite of Res it accepts a string that will extract a property from
 * the response object. Ex. 'records' will take api response records property
 */
export type QueryConfigPickRes<QueryFnType extends (...args: any) => any, Pick extends string = '', Res = ExtractFnReturnType<QueryFnType, Pick>> = Optional<Overwrite<UseQueryOptions<ExtractFnReturnType<QueryFnType, Pick>, AxiosError<CommutifiErrorData>, Res>, {
  meta?: QMeta;
}>, 'queryKey' | 'queryFn'>;

/**
 * Enum for specifying which parameter position contains the mutation body.
 * Used with MutationConfig to auto-infer body types from API function signatures.
 *
 * @example
 * // Body is the first parameter: deleteWorksite(worksiteId)
 * MutationConfig<typeof deleteWorksite>  // defaults to BodyParam.First
 *
 * // Body is the second parameter: postBuilding(enterpriseId, body)
 * MutationConfig<typeof postBuilding, BodyParam.Second>
 */
export enum BodyParam {
  /** Body is the first parameter (index 0) - default */
  First = 'first',
  /** Body is the second parameter (index 1) */
  Second = 'second',
  /** Body is the third parameter (index 2) */
  Third = 'third',
}
type BodyParamPosition = `${BodyParam}`;
type PositionToIndex = {
  first: 0;
  second: 1;
  third: 2;
};

/**
 * Resolves the body type for MutationConfig.
 * - If BodyOrPosition is a position string ('first', 'second', 'third'), extracts the parameter at that position
 * - Otherwise, uses BodyOrPosition directly as the body type (backward compatibility)
 */
type ResolveBodyType<MutationFnType extends (...args: any) => any, BodyOrPosition> = BodyOrPosition extends BodyParamPosition ? Parameters<MutationFnType>[PositionToIndex[Extract<BodyOrPosition, BodyParamPosition>]] : BodyOrPosition;

/**
 * MutationConfig utility type for typing mutation hooks.
 *
 * @param MutationFnType - The API function type
 * @param BodyOrPosition - Either a position indicator ('first', 'second', 'third', BodyParam.*)
 *                         OR a direct body type for custom mutationFn implementations
 *
 * @example
 * // Auto-infer from first param (default) - e.g., deleteWorksite(worksiteId)
 * MutationConfig<typeof deleteWorksite>
 *
 * @example
 * // Auto-infer from second param - e.g., postBuilding(enterpriseId, body)
 * MutationConfig<typeof postBuilding, BodyParam.Second>
 *
 * @example
 * // Custom body type for wrapped mutations - e.g., custom mutationFn with different signature
 * MutationConfig<typeof someApiFunc, { accountId: string; data: SomeType }>
 */
export type MutationConfig<MutationFnType extends (...args: any) => any, BodyOrPosition = 'first', TOnMutateResult = unknown> = Optional<UseMutationOptions<ExtractFnReturnType<MutationFnType>, AxiosError<CommutifiErrorData>, ResolveBodyType<MutationFnType, BodyOrPosition>, TOnMutateResult>, 'mutationKey' | 'mutationFn'>;

/**
 * Idem to MutationConfig but accepts a string to extract a property from the response object.
 * Ex. 'records' will take api response records property
 *
 * @param MutationFnType - The API function type
 * @param Pick - Property to extract from response
 * @param BodyOrPosition - Either a position indicator ('first', 'second', 'third', BodyParam.*)
 *                         OR a direct body type for custom mutationFn implementations
 *
 * @example
 * // Auto-infer from first param (default) - e.g., deleteWorksite(worksiteId)
 * MutationConfigPickRes<typeof deleteWorksite, 'data'>
 *
 * @example
 * // Auto-infer from second param - e.g., postBuilding(enterpriseId, body)
 * MutationConfigPickRes<typeof postBuilding, 'records', BodyParam.Second>
 *
 * @example
 * // Custom body type for wrapped mutations - e.g., custom mutationFn with different signature
 * MutationConfigPickRes<typeof someApiFunc, 'data', { accountId: string; data: SomeType }>
 */
export type MutationConfigPickRes<MutationFnType extends (...args: any) => any, Pick extends string = '', BodyOrPosition = 'first'> = Optional<Overwrite<UseMutationOptions<ExtractFnReturnType<MutationFnType, Pick>, AxiosError<CommutifiErrorData>, ResolveBodyType<MutationFnType, BodyOrPosition>>, {
  meta?: QMeta;
}>, 'mutationKey' | 'mutationFn'>;
export type InfiniteQueryOptions<QueryFnType extends (...args: any) => any> = UseInfiniteQueryOptions<ExtractFnReturnType<QueryFnType>, CommutifiError, InfiniteData<ExtractFnReturnType<QueryFnType>>, QueryKey, number | undefined>;
export type QueryResult<TData = unknown> = UseQueryResult<TData, AxiosError<CommutifiErrorData>>;
export function useQuery(options, queryClient) {
  return useQueryInternal(options, queryClient);
}
export function useInfiniteQuery(options, queryClient) {
  return useInfiniteQueryInternal(options, queryClient);
}
export function useMutation(options, queryClient) {
  return useMutationInternal(options, queryClient);
}
export const useReactQueryOnError = () => {
  const $ = _c(3);
  const {
    handleError,
    error: errorNotification
  } = useNotifications();
  let t0;
  if ($[0] !== errorNotification || $[1] !== handleError) {
    t0 = (error, query) => {
      if (query.meta?.shouldHandleError || query.meta?.handleErrorConfig) {
        handleError(error, query.meta.handleErrorConfig);
      } else {
        if (query.meta?.errorMessage) {
          errorNotification({
            description: query.meta.errorMessage as string
          });
        } else {
          if (query.meta?.errorConfig) {
            errorNotification(query.meta.errorConfig);
          }
        }
      }
    };
    $[0] = errorNotification;
    $[1] = handleError;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const useQueryClient = (...args: Parameters<typeof useQueryClientInternal>) => useQueryClientInternal(...args);